import React, { useCallback, useState } from "react"
import styled, { useTheme } from '~/ui/styled'
import { StartFlex, ColumnFlex, Text, IconFont } from "~components/El"
import { DialogChatContent } from "~components/PopupComponents"
import { Avatar } from "~widgets/avatar"
// import { OnlineStatus } from "~widgets/online-status"
import { MessageProps } from "./message.props"
import { observer } from "mobx-react-lite"
import MessageSent from "./message-sent"
import MessageReceived from "./message-received"
import { formatDate } from "~utils"
import { DeviceEventEmitter } from "~/ui/primitives"
import { REFRESH_CHANNEL_LIST_EVENT } from "~pages/app-stack/channel-list/constants"
import { CardType } from "~proto"
import { useModal } from "~hooks/useModal"
import { useStores } from "~models"
import useGroupActions from "~hooks/useGroupActions"
import useNavigation from "~hooks/useNavigation"
import { globalToast } from "~utils"

const Content = styled.Pressable`
  width: 160px;
  height: auto;
  padding: 10px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.blockBackground};
`
const titleIconMap = {
  [CardType.PRIVATE]: "nc",
  [CardType.GROUP]: "cjql",
  [CardType.CHANNEL]: "cjpd",
}
const titleMap = {
  [CardType.PRIVATE]: "个人名片",
  [CardType.GROUP]: "群聊名片",
  [CardType.CHANNEL]: "频道名片",
}
const confirmTextMap = {
  [CardType.PRIVATE]: "添加好友",
  [CardType.GROUP]: "加入群聊",
  [CardType.CHANNEL]: "订阅频道",
}

// 分享名片消息
export const BusinessCardMessage: React.FC<MessageProps & { content?: string }> = observer(
  ({ message, content }) => {
    const theme = useTheme()
    const navigation = useNavigation()
    const { userStore } = useStores()
    const currentUser = userStore.currentUser
    const { Dialog } = useModal()
    const { handleAddFriend, handleAddGroupMember, handleGetInfo, ensureLocalChatWhenJoiningGroup } =
      useGroupActions()
    const { attach } = message
    const [cardTitle, setCardTitle] = useState(message.content)
    const [cardSubContent, setCardSubContent] = useState("")
    const [cardAvatar, setCardAvatar] = useState("")
    const [isLoading, setIsLoading] = useState(false)

    const params: any = {
      type: attach?.card_info?.type,
      chat_cid: attach?.card_info?.chat_cid,
    }
    if (attach?.card_info?.type === CardType.PRIVATE) {
      params.user_id = attach?.card_info?.id
    } else if (attach?.card_info?.type === CardType.GROUP) {
      params.chat_id = Number(attach?.card_info?.id)
    } else if (attach?.card_info?.type === CardType.CHANNEL) {
      params.chat_id = Number(attach?.card_info?.id)
    }

    // 添加好友
    const handleAddContact = useCallback(async (item) => {
      return handleAddFriend({
        username: item?.user_info?.name,
        user_id: item?.user_id
      }).then(data => {
        globalToast.show("添加好友成功")
        navigation.goBack()
      }).catch(error => {
        globalToast.show(error?.message || "添加好友失败")
      })
    }, [])


  // 加入群聊
  const handleAddGroup = useCallback(
    async (cardInfo: any, infoData: any) => {
      ensureLocalChatWhenJoiningGroup({
        id: Number(cardInfo?.id),
        chat_id: String(cardInfo?.chat_cid),
        title: infoData?.group_info?.title,
        image: infoData?.group_info?.image,
        isChannel: false,
      })
      return handleAddGroupMember({
        chat_id: Number(cardInfo?.id),
        chat_cid: cardInfo?.chat_cid,
        user_ids: [currentUser.id],
        sender_id: cardInfo?.sender_id,
      })
        .then(() => {
          globalToast.show("加入群聊成功")
          navigation.goBack()
        })
        .catch((error) => {
          globalToast.show(error?.message || "加入群聊失败")
        })
    },
    [currentUser.id, ensureLocalChatWhenJoiningGroup, handleAddGroupMember, navigation],
  )

  // 订阅频道
  const handleJoinChannel = useCallback(
    async (cardInfo: any, infoData: any) => {
      ensureLocalChatWhenJoiningGroup({
        id: Number(cardInfo?.id),
        chat_id: String(cardInfo?.chat_cid),
        title: infoData?.channel_info?.title,
        image: infoData?.channel_info?.image,
        isChannel: true,
      })
      return handleAddGroupMember({
        chat_id: Number(cardInfo?.id),
        chat_cid: cardInfo?.chat_cid,
        user_ids: [currentUser.id],
        sender_id: cardInfo?.sender_id,
        chat_type: 2,
      })
        .then(() => {
          globalToast.show("频道订阅成功")
          DeviceEventEmitter.emit(REFRESH_CHANNEL_LIST_EVENT)
          navigation.goBack()
        })
        .catch((error) => {
          globalToast.show(error?.message || "频道订阅失败")
        })
    },
    [currentUser.id, ensureLocalChatWhenJoiningGroup, handleAddGroupMember, navigation],
  )
   
  const handlePress = useCallback(() => {
    if (isLoading) return
    setIsLoading(true)
    handleGetInfo(params).then(data => {
      let subContent = ""
      let avatar = ""
      let name = ""
      let isAction = currentUser.id !== attach?.card_info?.sender_id
      if (data?.type === CardType.PRIVATE) {
        subContent = Boolean(data?.user_info?.is_online) ? "在线" : "离线"
        avatar = data?.user_info?.image
        name = data?.user_info?.name
        isAction = !data?.user_info?.is_joined
        setCardTitle(data?.user_info?.name)
        setCardSubContent(Boolean(data?.user_info?.is_online) ? "在线" : "离线")
        setCardAvatar(data?.user_info?.image)
      } else if (data?.type === CardType.GROUP) {
        subContent = `${data?.group_info?.member_count}名成员`
        avatar = data?.group_info?.image
        name = data?.group_info?.title
        isAction = !data?.group_info?.is_joined
        setCardTitle(data?.group_info?.title)
        setCardSubContent(`${data?.group_info?.member_count}名成员`)
        setCardAvatar(data?.group_info?.image)
      } else if (data?.type === CardType.CHANNEL) {
        subContent = `${data?.channel_info?.member_count}订阅者`
        avatar = data?.channel_info?.image
        name = data?.channel_info?.title
        isAction = !data?.channel_info?.is_joined
        setCardTitle(data?.channel_info?.title)
        setCardSubContent(`${data?.channel_info?.member_count}订阅者`)
        setCardAvatar(data?.channel_info?.image)
      }
      Dialog.show({
        title: "名片详情",
        closeOnAction: isAction,
        confirmOnAction: isAction,
        confirmText: confirmTextMap[data?.type],
        content: React.createElement(DialogChatContent, {
          content: name,
          subContent: subContent,
          avatarProps: {
            size: 36,
            name: name,
            avatar: avatar,
          }
        }),
        onConfirm: async () => {
          if (data?.type === CardType.PRIVATE) {
            await handleAddContact(data)
          } else if (data?.type === CardType.GROUP) {
            await handleAddGroup(attach?.card_info, data)
          } else if (data?.type === CardType.CHANNEL) {
            await handleJoinChannel(attach?.card_info, data)
          }
        }
      })
      setIsLoading(false)
    }).catch(error => {
      globalToast.show(error?.message || "获取信息失败")
      setIsLoading(false)
    })
    
  }, [])
  return (
      <Content onClick={handlePress}>
          <ColumnFlex>
              <StartFlex>
                  <IconFont name={titleIconMap[attach?.card_info?.type]} size={12} color={theme.colors.purpleBackground} />
                  <Text fontSize={theme.fontSizes.sm} color={theme.colors.secondary} ml="4px">{titleMap[attach?.card_info?.type]}</Text>
              </StartFlex>
              <StartFlex padding="8px 0">
                  <Avatar size={40} avatar={cardAvatar} name={cardTitle}/>
                  <ColumnFlex flex={1} ml="8px">
                      <Text numberOfLines={1} fontSize={theme.fontSizes.md} color={theme.colors.text}>{cardTitle|| ""}</Text>
                      <Text numberOfLines={1} color={theme.colors.secondaryText} fontSize={theme.fontSizes.xs}>{cardSubContent}</Text>
                  </ColumnFlex>
              </StartFlex>
              <StartFlex>
                  <Text color={theme.colors.secondaryText} fontSize={theme.fontSizes.sm}>{formatDate(message.created, "hh:mm")}</Text>
                  <Text color={theme.colors.primaryText} fontSize={theme.fontSizes.xs} ml="20px">点击查看详情</Text>
              </StartFlex>
                  
              </ColumnFlex>
          </Content>
      )
  },
)

export const WrappedBusinessCardMessage: React.FC<MessageProps & { content?: string }> = (props) => {
    return props.message.byMe ? (
      <MessageSent {...props} Component={BusinessCardMessage} isNoBg={true} />
    ) : (
      <MessageReceived {...props} Component={BusinessCardMessage} isNoBg={true} />
    )
  }
  export default WrappedBusinessCardMessage
